import * as yup from 'yup';
import { TFunction } from 'react-i18next';

/**
 * Pool create/edit validation, single-sourced in common so both pool surfaces — the accordion
 * `PoolForm` and the standalone `PunchupPoolDetails` sub-page — enforce one rule set (mirrors
 * TicketDetails/utils.ts `validationSchema`). It lives here, below both consumers, so neither
 * reaches across the feature tree for it.
 *
 * `getSoldFloor` is read at validation time (a getter over a ref) so the Lifetime-Σ floor reflects the
 * settled member selection, not capture time — see docs/pools_business_logic.md T-3. The sold-floor
 * message is a backstop: the field also clamps to the floor on blur (and shows a hint), so this rule
 * fires only if a sub-floor value reaches submit some other way.
 */
export const buildPoolSchema = (t: TFunction<'translation', undefined>, getSoldFloor: () => number = () => 0) =>
	yup.object({
		name: yup.string().required(t('poolDetails-name-required')),
		description: yup.string(),
		total_amount: yup
			.number()
			.typeError(t('poolDetails-total-required'))
			.required(t('poolDetails-total-required'))
			.min(0, t('poolDetails-total-min'))
			.test('above-sold', '', function (val) {
				const floor = getSoldFloor();
				if ((val ?? 0) >= floor) return true;
				return this.createError({
					message: t('poolDetails-total-min-sold', "Can't be lower than the {{count}} tickets already sold.", {
						count: floor,
					}),
				});
			}),
		// A pool caps a shared capacity across one or more tiers — an empty pool caps nothing. The
		// backend returns no error here (UX guard); surfaced under the checklist in PoolForm.
		ticket_type_ids: yup
			.array()
			.of(yup.string())
			.min(1, t('poolDetails-ticketTypes-required', 'Pick at least one ticket type.'))
			.default([]),
	});
